/**
 * StorageService.ts
 *
 * Service unifié pour gérer Supabase Storage avec cache de signed URLs
 * PHASE 2: Supabase Storage - Standardisation des paths et signed URLs
 *
 * - Stocke storage_path en DB (sans préfixe bucket)
 * - Génère signed URLs au runtime avec cache
 * - Ne jamais utiliser getPublicUrl si bucket public=false
 */

import { supabase } from './SupabaseService';
import { logger } from '../utils/ProfessionalLogger';

/**
 * Interface pour les métadonnées d'un fichier uploadé
 */
export interface UploadFileMetadata {
  storage_path: string;
  size_bytes?: number;
  mime_type?: string;
  [key: string]: unknown;
}

/**
 * Interface pour une entrée de cache
 */
interface CachedSignedUrl {
  url: string;
  expiresAtEpoch: number; // Timestamp en millisecondes
}

/**
 * Cache en mémoire pour les signed URLs avec LRU eviction
 * Clé: `${bucket}:${storagePath}`
 * Valeur: { url, expiresAtEpoch, lastAccessed }
 */
interface CachedSignedUrlWithAccess extends CachedSignedUrl {
  lastAccessed: number; // Timestamp de dernier accès pour LRU
}

const signedUrlCache = new Map<string, CachedSignedUrlWithAccess>();

/**
 * TTL par défaut pour les signed URLs (1 heure)
 */
const DEFAULT_SIGNED_URL_TTL = 3600; // secondes

/**
 * Marge de sécurité pour le cache (10% avant expiration réelle)
 */
const CACHE_MARGIN_PERCENT = 0.1;

/**
 * Limite maximale du cache (nombre d'entrées)
 * Au-delà, on utilise LRU eviction
 */
const MAX_CACHE_SIZE = 1000;

/**
 * Intervalle de nettoyage périodique (5 minutes)
 */
const CLEANUP_INTERVAL_MS = 5 * 60 * 1000;

/**
 * ID de l'intervalle de nettoyage périodique
 */
let cleanupIntervalId: ReturnType<typeof setInterval> | null = null;

/**
 * Métriques du cache
 */
interface CacheMetrics {
  hits: number;
  misses: number;
  evictions: number;
  size: number;
}

const cacheMetrics: CacheMetrics = {
  hits: 0,
  misses: 0,
  evictions: 0,
  size: 0,
};

/**
 * Nettoie le cache des URLs expirées
 * Optimisé : ne parcourt que si nécessaire
 */
function cleanupExpiredCache(): void {
  const now = Date.now();
  let cleaned = 0;

  for (const [key, cached] of signedUrlCache.entries()) {
    if (now >= cached.expiresAtEpoch) {
      signedUrlCache.delete(key);
      cleaned++;
    }
  }

  if (cleaned > 0) {
    cacheMetrics.size = signedUrlCache.size;
    logger.debug(
      `🧹 Cache nettoyé: ${cleaned} entrée(s) expirée(s), ${signedUrlCache.size} restante(s)`
    );
  }
}

/**
 * Nettoie le cache avec LRU eviction si nécessaire
 */
function evictLRUIfNeeded(): void {
  if (signedUrlCache.size <= MAX_CACHE_SIZE) {
    return;
  }

  // Trier par lastAccessed (plus ancien en premier)
  const entries = Array.from(signedUrlCache.entries()).sort(
    (a, b) => a[1].lastAccessed - b[1].lastAccessed
  );

  // Supprimer les 10% les plus anciens
  const toEvict = Math.ceil(entries.length * 0.1);
  for (let i = 0; i < toEvict; i++) {
    signedUrlCache.delete(entries[i][0]);
  }

  cacheMetrics.evictions += toEvict;
  cacheMetrics.size = signedUrlCache.size;
  logger.debug(
    `🗑️ LRU eviction: ${toEvict} entrée(s) supprimée(s), ${signedUrlCache.size} restante(s)`
  );
}

/**
 * Démarre le nettoyage périodique du cache
 */
function startPeriodicCleanup(): void {
  if (cleanupIntervalId !== null) {
    return; // Déjà démarré
  }

  cleanupIntervalId = setInterval(() => {
    cleanupExpiredCache();
    evictLRUIfNeeded();
  }, CLEANUP_INTERVAL_MS);

  logger.debug('🔄 Nettoyage périodique du cache démarré');
}

/**
 * Arrête le nettoyage périodique du cache
 */
function stopPeriodicCleanup(): void {
  if (cleanupIntervalId !== null) {
    clearInterval(cleanupIntervalId);
    cleanupIntervalId = null;
    logger.debug('⏹️ Nettoyage périodique du cache arrêté');
  }
}

/**
 * Service de gestion du stockage Supabase
 */
export class StorageService {
  /**
   * Upload un fichier vers Supabase Storage
   *
   * @param bucket - Nom du bucket (ex: 'template-editor-uploads')
   * @param storagePath - Chemin de stockage (sans préfixe bucket, ex: 'userId/projectId/uploads/file.png')
   * @param file - Fichier à uploader
   * @param contentType - Type MIME du fichier (optionnel)
   * @returns { storage_path, metadata } ou throw une erreur
   */
  static async uploadFile(
    bucket: string,
    storagePath: string,
    file: File | Blob,
    contentType?: string
  ): Promise<{ storage_path: string; metadata: UploadFileMetadata }> {
    if (!supabase) {
      throw new Error('Supabase client non disponible');
    }

    try {
      // Vérifier que le chemin ne contient pas de préfixe bucket
      if (storagePath.includes(bucket)) {
        throw new Error(
          `Le storage_path ne doit pas contenir le nom du bucket. Path: ${storagePath}`
        );
      }

      // PHASE 2: Améliorer la validation RLS - vérifier que le premier segment est un UUID valide
      const pathSegments = storagePath.split('/');
      const firstSegment = pathSegments[0];

      if (!firstSegment) {
        throw new Error(
          `Le storage_path doit commencer par un userId (UUID). Path: ${storagePath}`
        );
      }

      // Vérifier le format UUID (8-4-4-4-12 hexadécimal)
      // Format UUID v4: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(firstSegment)) {
        // Exception pour les templates publics (templateId peut ne pas être un UUID)
        if (bucket === 'template-editor-templates') {
          logger.debug(`Template public détecté, validation UUID ignorée: ${storagePath}`);
        } else {
          throw new Error(
            `Le premier segment du storage_path doit être un UUID valide (userId) pour la conformité RLS. ` +
              `Reçu: "${firstSegment}" dans le path: ${storagePath}`
          );
        }
      }

      // Vérifier que le chemin a au moins 2 segments (userId + au moins un autre)
      if (pathSegments.length < 2 && bucket !== 'template-editor-templates') {
        logger.warn(
          `Le storage_path devrait avoir au moins 2 segments (userId + ...). Path: ${storagePath}`
        );
      }

      const uploadOptions: { contentType?: string; upsert?: boolean } = {};
      if (contentType) {
        uploadOptions.contentType = contentType;
      }

      const { error } = await supabase.storage
        .from(bucket)
        .upload(storagePath, file, uploadOptions);

      if (error) {
        logger.error(`Erreur lors de l'upload vers ${bucket}/${storagePath}:`, error);
        throw new Error(`Erreur upload: ${error.message}`);
      }

      // Construire les métadonnées
      const metadata: UploadFileMetadata = {
        storage_path: storagePath,
        size_bytes: file.size,
        mime_type: contentType || (file instanceof File ? file.type : undefined),
      };

      logger.info(`✅ Fichier uploadé: ${bucket}/${storagePath} (${file.size} bytes)`);

      return {
        storage_path: storagePath,
        metadata,
      };
    } catch (error) {
      logger.error(`Exception lors de l'upload vers ${bucket}/${storagePath}:`, error);
      throw error;
    }
  }

  /**
   * Crée une signed URL pour un fichier dans Supabase Storage
   * Utilise le cache pour éviter de re-signer en boucle
   *
   * @param bucket - Nom du bucket
   * @param storagePath - Chemin de stockage (sans préfixe bucket)
   * @param expiresInSeconds - TTL en secondes (défaut: 1 heure)
   * @returns Signed URL
   */
  static async createSignedUrl(
    bucket: string,
    storagePath: string,
    expiresInSeconds: number = DEFAULT_SIGNED_URL_TTL
  ): Promise<string> {
    if (!supabase) {
      throw new Error('Supabase client non disponible');
    }

    // Nettoyer le cache expiré périodiquement
    cleanupExpiredCache();

    // Clé de cache
    const cacheKey = `${bucket}:${storagePath}`;

    // Démarrer le nettoyage périodique si pas déjà démarré
    startPeriodicCleanup();

    // Vérifier le cache
    const cached = signedUrlCache.get(cacheKey);
    if (cached) {
      const now = Date.now();
      // Utiliser une marge de 10% avant expiration réelle
      const expiresAtWithMargin =
        cached.expiresAtEpoch - expiresInSeconds * 1000 * CACHE_MARGIN_PERCENT;

      if (now < expiresAtWithMargin) {
        // Mettre à jour lastAccessed pour LRU
        cached.lastAccessed = now;
        cacheMetrics.hits++;
        logger.debug(`✅ Signed URL depuis cache: ${bucket}/${storagePath}`);
        return cached.url;
      }
      // Cache expiré, on le supprime
      signedUrlCache.delete(cacheKey);
      cacheMetrics.size = signedUrlCache.size;
    }

    cacheMetrics.misses++;

    try {
      // Créer la signed URL
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(storagePath, expiresInSeconds);

      if (error) {
        logger.error(
          `Erreur lors de la création de la signed URL pour ${bucket}/${storagePath}:`,
          error
        );
        throw new Error(`Erreur signed URL: ${error.message}`);
      }

      if (!data?.signedUrl) {
        throw new Error('Signed URL non retournée par Supabase');
      }

      // Mettre en cache avec lastAccessed pour LRU
      const now = Date.now();
      const expiresAtEpoch = now + expiresInSeconds * 1000;
      signedUrlCache.set(cacheKey, {
        url: data.signedUrl,
        expiresAtEpoch,
        lastAccessed: now,
      });

      cacheMetrics.size = signedUrlCache.size;

      // Vérifier si on doit faire une eviction LRU
      evictLRUIfNeeded();

      logger.debug(`✅ Signed URL créée: ${bucket}/${storagePath} (TTL: ${expiresInSeconds}s)`);

      return data.signedUrl;
    } catch (error) {
      logger.error(
        `Exception lors de la création de la signed URL pour ${bucket}/${storagePath}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Supprime un ou plusieurs fichiers de Supabase Storage
   *
   * @param bucket - Nom du bucket
   * @param storagePath - Chemin de stockage (sans préfixe bucket) ou tableau de chemins
   */
  static async deleteFile(bucket: string, storagePath: string | string[]): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase client non disponible');
    }

    try {
      const paths = Array.isArray(storagePath) ? storagePath : [storagePath];

      if (paths.length === 0) {
        return;
      }

      const { error } = await supabase.storage.from(bucket).remove(paths);

      if (error) {
        logger.error(
          `Erreur lors de la suppression de ${paths.length} fichier(s) dans ${bucket}:`,
          error
        );
        throw new Error(`Erreur suppression: ${error.message}`);
      }

      // Invalider le cache pour tous les fichiers supprimés
      for (const path of paths) {
        const cacheKey = `${bucket}:${path}`;
        signedUrlCache.delete(cacheKey);
      }

      logger.info(`✅ ${paths.length} fichier(s) supprimé(s) dans ${bucket}`);
    } catch (error) {
      logger.error(`Exception lors de la suppression dans ${bucket}:`, error);
      throw error;
    }
  }

  /**
   * Invalide le cache pour un fichier spécifique ou tous les fichiers
   *
   * @param bucket - Nom du bucket (optionnel)
   * @param storagePath - Chemin de stockage (optionnel)
   */
  static invalidateCache(bucket?: string, storagePath?: string): void {
    if (bucket && storagePath) {
      const cacheKey = `${bucket}:${storagePath}`;
      signedUrlCache.delete(cacheKey);
      logger.debug(`Cache invalidé: ${cacheKey}`);
    } else {
      signedUrlCache.clear();
      logger.debug('Cache complètement invalidé');
    }
  }

  /**
   * Nettoie le cache des URLs expirées
   */
  static cleanupCache(): void {
    cleanupExpiredCache();
    evictLRUIfNeeded();
  }

  /**
   * Obtient les métriques du cache
   */
  static getCacheMetrics(): CacheMetrics & { hitRate: number } {
    const total = cacheMetrics.hits + cacheMetrics.misses;
    const hitRate = total > 0 ? cacheMetrics.hits / total : 0;

    return {
      ...cacheMetrics,
      size: signedUrlCache.size,
      hitRate,
    };
  }

  /**
   * Réinitialise les métriques du cache
   */
  static resetCacheMetrics(): void {
    cacheMetrics.hits = 0;
    cacheMetrics.misses = 0;
    cacheMetrics.evictions = 0;
    cacheMetrics.size = signedUrlCache.size;
  }

  /**
   * Arrête le nettoyage périodique (utile pour les tests)
   */
  static stopPeriodicCleanup(): void {
    stopPeriodicCleanup();
  }
}
